import { Box, Tag, TagCloseButton, TagLabel } from '@chakra-ui/react';
import { Tooltip } from '@main/complykit/components/tooltip';
import { Focusable } from 'react-aria-components';

import { Combobox, ComboboxProps, withMultiCombobox } from '../combobox';
import { AvatarOption, AvatarOptionIcon, AvatarOptionProps } from './avatar-option';

export type { AvatarOption, AvatarOptionProps, AvatarOptionVariant } from './avatar-option';

export const defaultProps = {
  getOptionValue: (option) => option.id,
  getOptionLabel: (option) => option.displayName,

  components: {
    Option: ({
      id,
      displayName,
      avatarUrl,
      kind,
      variant = 'detailed',
      size = 'xs',
      removeProps,
    }) => {
      const icon = (
        <AvatarOptionIcon size={size} displayName={displayName} avatarUrl={avatarUrl} kind={kind} />
      );
      return (
        <Tag bg="none" minW="auto" maxW="none" px={0}>
          {/* The detailed variant shows the name beside the avatar, so there is
           * no tooltip and nothing for a tab stop to do. */}
          {variant === 'detailed' ? (
            icon
          ) : (
            <Tooltip content={displayName} placement="top">
              <Focusable>
                {/* The plain-trigger role the lib's tooltip convention uses. */}
                <span role="button">{icon}</span>
              </Focusable>
            </Tooltip>
          )}
          {variant === 'detailed' && (
            <TagLabel ml={2} fontSize="xs" color="gray.500">
              {displayName}
            </TagLabel>
          )}
          {removeProps && (
            <Box as={TagCloseButton} {...removeProps} aria-label={`Remove ${displayName}`} />
          )}
        </Tag>
      );
    },
  },
} satisfies ComboboxProps<AvatarOptionProps>;

export function EditableAvatar<TOption extends AvatarOption, IsMulti extends boolean = false>(
  props: Omit<ComboboxProps<TOption, IsMulti>, 'components'>,
) {
  return <Combobox {...defaultProps} {...withMultiCombobox(props)} />;
}

EditableAvatar.Option = defaultProps.components.Option;
